'use client';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import toast from 'react-hot-toast';
import { useT, useApiLang } from '@/lib/useT';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import PageAtmosphere from '@/components/PageAtmosphere';
import { Calendar, Tag, Search, X, Sparkles, BookOpen, ArrowRight, Newspaper, Filter, Mail, Rss, Loader2 } from 'lucide-react';

interface BlogSummary {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string | null;
  tags: string[];
  author: string;
  publishedAt: string;
  wordCount?: number;
}

function tagColor(_tag: string): string {
  // On-brand signal-yellow for every tag chip / card accent — no per-tag
  // rainbow (purple/teal/pink looked off-brand next to the black+yellow design).
  // Hex (not var) so call-sites that append an alpha — `${tagColor()}40` — stay valid.
  return 'var(--accent)';
}

function readingMinutes(words?: number): number {
  if (!words || words < 1) return 1;
  return Math.max(1, Math.round(words / 200));
}

function fmtDate(s: string, locale: string) {
  return new Date(s).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

// Suspense wrapper — useSearchParams() requires it for prerendering in Next 14.
export default function BlogPage() {
  return (
    <Suspense fallback={<div className="min-h-screen" style={{ background: 'var(--c-bg)' }} />}>
      <BlogListing />
    </Suspense>
  );
}

function BlogListing() {
  const t = useT();
  const apiLang = useApiLang();
  const search = useSearchParams();
  const locale = ({ de: 'de-DE', en: 'en-US', ru: 'ru-RU', ua: 'uk-UA', pl: 'pl-PL', cz: 'cs-CZ', md: 'ro-RO', bg: 'bg-BG', kz: 'kk-KZ' } as Record<string, string>)[apiLang] || 'de-DE';
  const [posts, setPosts] = useState<BlogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(search?.get('tag') || null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const apiBase = process.env.NEXT_PUBLIC_API_URL || '/api';

  // Sync URL → state when navigating between blog tag links.
  useEffect(() => { setActiveTag(search?.get('tag') || null); }, [search]);

  // Initial / lang-change load. Always replaces the list, resets cursor.
  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${apiBase}/public/blog?lang=${encodeURIComponent(apiLang)}&take=12`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        // Backward-compat: API now returns { items, nextCursor }, but
        // older deploys may still return a flat array. Handle both.
        if (Array.isArray(data)) {
          setPosts(data);
          setNextCursor(null);
        } else {
          setPosts(Array.isArray(data?.items) ? data.items : []);
          setNextCursor(data?.nextCursor || null);
        }
      })
      .catch(() => { setPosts([]); setNextCursor(null); setError(true); })
      .finally(() => setLoading(false));
  }, [apiLang, apiBase]);

  // "Load more" — append next page, keep current cursor.
  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await fetch(`${apiBase}/public/blog?lang=${encodeURIComponent(apiLang)}&take=12&cursor=${encodeURIComponent(nextCursor)}`);
      const data = await r.json();
      const more: BlogSummary[] = Array.isArray(data) ? data : (data?.items || []);
      setPosts(prev => [...prev, ...more]);
      setNextCursor(Array.isArray(data) ? null : (data?.nextCursor || null));
    } catch {/* keep state */} finally { setLoadingMore(false); }
  };

  // Tag list with counts, sorted by frequency.
  const tagCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of posts) for (const tag of p.tags) counts.set(tag, (counts.get(tag) || 0) + 1);
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([tag, count]) => ({ tag, count }));
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter(p => {
      if (activeTag && !p.tags.includes(activeTag)) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      );
    });
  }, [posts, activeTag, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--c-bg)' }}>
      {/* Animated gradient blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="absolute top-[-200px] left-[10%] w-[500px] h-[500px] rounded-full"
          style={{ background: 'var(--accent)', filter: 'blur(180px)', opacity: 0.07, animation: 'float 24s ease-in-out infinite' }} />
        <div className="absolute top-[20%] right-[5%] w-[400px] h-[400px] rounded-full"
          style={{ background: 'var(--accent)', filter: 'blur(160px)', opacity: 0.06, animation: 'float 30s ease-in-out infinite reverse' }} />
        <div className="absolute bottom-[10%] left-[30%] w-[450px] h-[450px] rounded-full"
          style={{ background: 'var(--accent)', filter: 'blur(170px)', opacity: 0.05, animation: 'float 26s ease-in-out infinite' }} />
      </div>

      <div className="relative" style={{ zIndex: 1 }}>
        <PublicHeader />
      <PageAtmosphere />

        {/* ─── Hero ─── */}
        <section className="px-6 md:px-8 pt-24 md:pt-28 pb-8 max-w-6xl mx-auto">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 text-xs font-semibold uppercase tracking-wider"
              style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)' }}>
              <Newspaper size={12} />
              {t('blog_title')}
            </div>
            <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.18] tracking-tight mb-5"
              style={{
                background: 'linear-gradient(135deg, var(--c-heading) 0%, var(--accent) 50%, var(--accent) 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                paddingBottom: '0.12em',
              }}>
              {t('blog_title')}
            </h1>
            <p className="text-lg md:text-xl max-w-2xl leading-relaxed" style={{ color: 'var(--c-muted)' }}>
              {t('blog_intro')}
            </p>

            {/* Stat strip */}
            {!loading && posts.length > 0 && (
              <div className="mt-7 flex items-center gap-5 text-sm flex-wrap" style={{ color: 'var(--c-muted)' }}>
                <span className="flex items-center gap-1.5">
                  <BookOpen size={14} style={{ color: 'var(--accent)' }} />
                  <b style={{ color: 'var(--c-heading)' }}>{posts.length}</b> {t('blog_post_count')}
                </span>
                <span className="flex items-center gap-1.5">
                  <Tag size={14} style={{ color: 'var(--accent)' }} />
                  <b style={{ color: 'var(--c-heading)' }}>{tagCounts.length}</b> Tags
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ─── Search + tag chips ─── */}
        <section className="px-6 md:px-8 pb-8 max-w-6xl mx-auto">
          <div className="logixa-card p-4 md:p-5 flex flex-col md:flex-row gap-3 md:items-center"
            style={{ background: 'var(--c-panel)' }}>
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--c-muted)' }} />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('blog_search_ph')}
                className="w-full pl-10 pr-9 py-2.5 rounded-xl text-sm outline-none transition-colors"
                style={{ background: 'var(--c-bg)', color: 'var(--c-text)', border: '1px solid var(--c-border)' }}
              />
              {query && (
                <button onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/5"
                  style={{ color: 'var(--c-muted)' }}>
                  <X size={14} />
                </button>
              )}
            </div>
            {(query || activeTag) && (
              <button onClick={() => { setQuery(''); setActiveTag(null); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:scale-[1.03]"
                style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)' }}>
                <Filter size={12} />
                {t('blog_clear_filters')}
              </button>
            )}
          </div>

          {/* Tag chips */}
          {tagCounts.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              <TagChip
                label={t('blog_filter_all')}
                count={posts.length}
                color="var(--accent)"
                active={!activeTag}
                onClick={() => setActiveTag(null)}
              />
              {tagCounts.map(({ tag, count }) => (
                <TagChip
                  key={tag}
                  label={'#' + tag}
                  count={count}
                  color={tagColor(tag)}
                  active={activeTag === tag}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                />
              ))}
            </div>
          )}
        </section>

        {/* ─── Posts ─── */}
        <section className="px-6 md:px-8 pb-20 max-w-6xl mx-auto">
          {loading ? (
            <PostSkeletons />
          ) : error ? (
            <div className="logixa-card p-10 text-center" style={{ background: 'var(--c-panel)' }}>
              <p style={{ color: 'var(--c-muted)' }}>—</p>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              hasFilters={!!(query || activeTag)}
              onClear={() => { setQuery(''); setActiveTag(null); }}
              empty={posts.length === 0 ? t('blog_empty') : t('blog_no_results')}
              clearLabel={t('blog_clear_filters')}
            />
          ) : (
            <>
              {featured && (
                <FeaturedCard post={featured} t={t} locale={locale} onTagClick={setActiveTag} />
              )}

              {rest.length > 0 && (
                <>
                  <h2 className="mt-14 mb-6 text-xl md:text-2xl font-display font-bold flex items-center gap-2"
                    style={{ color: 'var(--c-heading)' }}>
                    <Sparkles size={18} style={{ color: 'var(--accent)' }} />
                    {t('blog_latest')}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {rest.map((p, i) => (
                      <PostCard key={p.slug} post={p} t={t} locale={locale}
                        delayMs={i * 60} onTagClick={setActiveTag} />
                    ))}
                  </div>
                </>
              )}

              {/* Load more — only when API tells us there's a next page AND
                  no client-side filter is active (cursor pagination doesn't
                  combine with full-text/tag filters; those work over the
                  already-loaded set). */}
              {nextCursor && !activeTag && !query && (
                <div className="mt-10 flex justify-center">
                  <button onClick={loadMore} disabled={loadingMore}
                    className="px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all hover:scale-[1.03] disabled:opacity-50"
                    style={{ background: 'var(--accent)', color: '#fff', boxShadow: '0 4px 12px color-mix(in srgb, var(--accent) 30%, transparent)' }}>
                    {loadingMore ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    {t('blog_load_more')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {/* Subscribe + RSS strip */}
        <section className="px-6 md:px-8 pb-20 max-w-4xl mx-auto">
          <SubscribeWidget t={t} apiBase={apiBase} apiLang={apiLang} />
        </section>

        <PublicFooter />
      </div>

      {/* Local keyframes for the floating blobs (animate-fade-up already in globals.css). */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(40px, -30px) scale(1.06); }
        }
      `}</style>
    </div>
  );
}

/* ───────── Sub-components ───────── */

function TagChip({ label, count, color, active, onClick }: {
  label: string; count: number; color: string; active: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick}
      className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all hover:scale-[1.04]"
      style={{
        background: active ? color : 'var(--c-panel)',
        color: active ? '#fff' : color,
        border: '1px solid ' + (active ? color : 'var(--c-border)'),
        boxShadow: active ? `0 4px 12px ${color}33` : 'none',
      }}>
      {label}
      <span className="text-[10px] opacity-80 font-mono">{count}</span>
    </button>
  );
}

function FeaturedCard({ post, t, locale, onTagClick }: {
  post: BlogSummary; t: (k: string) => string; locale: string;
  onTagClick: (tag: string) => void;
}) {
  const minRead = readingMinutes(post.wordCount);
  return (
    <Link href={`/blog/${post.slug}`} className="block group animate-fade-up">
      <article className="rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-0"
        style={{ background: 'var(--c-panel)', border: '1px solid var(--c-border)' }}>
        <div className="relative h-64 md:h-auto overflow-hidden">
          {post.coverImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={post.coverImage} alt=""
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(135deg, var(--accent) 0%, #A855F7 50%, var(--accent) 100%)',
            }} />
          )}
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)' }} />
          <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5"
            style={{ background: 'rgba(255,255,255,0.95)', color: 'var(--accent)', backdropFilter: 'blur(8px)' }}>
            <Sparkles size={10} /> {t('blog_featured')}
          </div>
        </div>
        <div className="p-7 md:p-9 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: 'var(--c-muted)' }}>
            <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(post.publishedAt, locale)}</span>
            <span>·</span>
            <span>{post.author}</span>
            <span>·</span>
            <span>{minRead} {t('blog_min_read')}</span>
          </div>
          <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight transition-colors group-hover:text-[var(--c-accent)]"
            style={{ color: 'var(--c-heading)' }}>
            {post.title}
          </h2>
          <p className="text-base leading-relaxed line-clamp-3" style={{ color: 'var(--c-text)' }}>
            {post.excerpt}
          </p>
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {post.tags.slice(0, 4).map(tg => {
                const c = tagColor(tg);
                return (
                  <span key={tg} onClick={(e) => { e.preventDefault(); onTagClick(tg); }}
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold cursor-pointer hover:scale-105 transition-transform"
                    style={{ background: `${c}18`, color: c, border: `1px solid ${c}44` }}>
                    #{tg}
                  </span>
                );
              })}
            </div>
          )}
          <div className="mt-auto pt-3 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: 'var(--accent)' }}>
            {t('blog_read_more')}
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </article>
    </Link>
  );
}

function PostCard({ post, t, locale, delayMs, onTagClick }: {
  post: BlogSummary; t: (k: string) => string; locale: string; delayMs: number;
  onTagClick: (tag: string) => void;
}) {
  const minRead = readingMinutes(post.wordCount);
  return (
    <Link href={`/blog/${post.slug}`} className="block group animate-fade-up"
      style={{ animationDelay: `${delayMs}ms`, animationFillMode: 'backwards' }}>
      <article className="h-full rounded-2xl overflow-hidden flex flex-col transition-transform duration-300 group-hover:-translate-y-1"
        style={{ background: 'var(--c-panel)', border: '1px solid var(--c-border)', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
        <div className="relative aspect-[16/9] overflow-hidden">
          {post.coverImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={post.coverImage} alt=""
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${tagColor(post.tags[0] || post.slug)}40 0%, ${tagColor(post.tags[1] || post.author)}30 100%)` }}>
              <Newspaper size={48} style={{ color: 'rgba(255,255,255,0.5)' }} />
            </div>
          )}
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.35) 100%)' }} />
          {post.tags[0] && (
            <span onClick={(e) => { e.preventDefault(); onTagClick(post.tags[0]); }}
              className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.95)', color: tagColor(post.tags[0]), backdropFilter: 'blur(8px)' }}>
              #{post.tags[0]}
            </span>
          )}
        </div>
        <div className="p-5 flex flex-col gap-3 flex-1">
          <h3 className="font-bold text-lg leading-snug line-clamp-2 transition-colors group-hover:text-[var(--c-accent)]"
            style={{ color: 'var(--c-heading)' }}>
            {post.title}
          </h3>
          <p className="text-sm leading-relaxed line-clamp-2 flex-1" style={{ color: 'var(--c-text)' }}>
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs pt-1" style={{ color: 'var(--c-muted)' }}>
            <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(post.publishedAt, locale)}</span>
            <span>{minRead} {t('blog_min_read')}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}

function PostSkeletons() {
  return (
    <>
      {/* Featured skeleton */}
      <div className="rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-0 mb-12 animate-pulse"
        style={{ background: 'var(--c-panel)', border: '1px solid var(--c-border)' }}>
        <div className="h-64 md:h-80" style={{ background: 'var(--c-hover)' }} />
        <div className="p-7 md:p-9 space-y-3">
          <div className="h-3 rounded w-1/3" style={{ background: 'var(--c-hover)' }} />
          <div className="h-7 rounded w-3/4" style={{ background: 'var(--c-hover)' }} />
          <div className="h-7 rounded w-2/3" style={{ background: 'var(--c-hover)' }} />
          <div className="h-3 rounded w-full" style={{ background: 'var(--c-hover)' }} />
          <div className="h-3 rounded w-5/6" style={{ background: 'var(--c-hover)' }} />
        </div>
      </div>
      {/* Card grid skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-2xl overflow-hidden animate-pulse"
            style={{ background: 'var(--c-panel)', border: '1px solid var(--c-border)' }}>
            <div className="aspect-[16/9]" style={{ background: 'var(--c-hover)' }} />
            <div className="p-5 space-y-2">
              <div className="h-5 rounded w-4/5" style={{ background: 'var(--c-hover)' }} />
              <div className="h-3 rounded w-full" style={{ background: 'var(--c-hover)' }} />
              <div className="h-3 rounded w-3/4" style={{ background: 'var(--c-hover)' }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function EmptyState({ hasFilters, onClear, empty, clearLabel }: {
  hasFilters: boolean; onClear: () => void; empty: string; clearLabel: string;
}) {
  return (
    <div className="logixa-card p-12 text-center" style={{ background: 'var(--c-panel)' }}>
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
        style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>
        <Newspaper size={28} style={{ color: 'var(--accent)' }} />
      </div>
      <p className="text-base mb-4" style={{ color: 'var(--c-muted)' }}>{empty}</p>
      {hasFilters && (
        <button onClick={onClear}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium"
          style={{ background: 'var(--accent)', color: '#fff' }}>
          <Filter size={13} /> {clearLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Email-subscription card. Single opt-in for now (no double-confirmation
 * email round-trip). RSS link sits in the footer so power users have an
 * alternative to inbox subscriptions.
 */
function SubscribeWidget({ t, apiBase, apiLang }: { t: (k: string) => string; apiBase: string; apiLang: string }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error(t('blog_subscribe_invalid_email'));
      inputRef.current?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${apiBase}/public/blog/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean, lang: apiLang }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDone(true);
      setEmail('');
    } catch {
      toast.error(t('blog_subscribe_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-3xl p-7 md:p-10 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent) 10%, transparent) 0%, color-mix(in srgb, var(--accent) 10%, transparent) 100%)', border: '1px solid var(--c-border)' }}>
      {/* Decorative glow */}
      <div className="absolute top-[-100px] right-[-50px] w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{ background: 'var(--accent)', filter: 'blur(140px)', opacity: 0.20 }} />

      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3 text-[10px] font-bold uppercase tracking-wider"
            style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)' }}>
            <Mail size={11} /> Newsletter
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl mb-2 leading-tight"
            style={{ color: 'var(--c-heading)' }}>
            {t('blog_subscribe_title')}
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--c-muted)' }}>
            {t('blog_subscribe_desc')}
          </p>
          <a href={`${apiBase}/public/blog/rss.xml`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ color: 'var(--accent)' }}>
            <Rss size={11} /> RSS Feed
          </a>
        </div>

        <div>
          {done ? (
            <div className="rounded-2xl p-5 flex items-center gap-3"
              style={{ background: 'color-mix(in srgb, var(--live) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--live) 30%, transparent)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'color-mix(in srgb, var(--live) 20%, transparent)' }}>
                <Mail size={18} style={{ color: 'var(--live)' }} />
              </div>
              <div className="text-sm">
                <p className="font-semibold" style={{ color: 'var(--c-heading)' }}>{t('blog_subscribe_thanks')}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--c-muted)' }}>{t('blog_subscribe_confirm')}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--c-muted)' }} />
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('blog_subscribe_email_ph')}
                  required
                  className="w-full pl-10 pr-3 py-3 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--c-panel)', color: 'var(--c-text)', border: '1px solid var(--c-border)' }}
                />
              </div>
              <button type="submit" disabled={submitting}
                className="px-5 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition-all hover:scale-[1.03] disabled:opacity-50"
                style={{ background: 'var(--accent)', color: '#fff', boxShadow: '0 4px 12px color-mix(in srgb, var(--accent) 30%, transparent)' }}>
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                {t('blog_subscribe_btn')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
